import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import MultipleQuestionModal from "./Modal/MultipleQuestionModal";
import SubjectiveQuestionModal from "./Modal/SubjectiveQuestionModal";
import { useRecoilValue } from "recoil";
import { gameProposalIdState } from "src/recoil/atoms/gameProposalState";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { LabsQuestionComposeAPI, LabsQuestionOverviewAPI, QuestionDetailAPI, QuestionsBySourceAPI } from "../../TestManagementApi";
import HamburgerIcon from 'src/components/common/icons/HamburgurIcon';
import LimitModal from './Modal/LimitModal';
import GenreModal from './Modal/GenreModal';
import CustomNavLink from '../components/CustomNavLink';
import { GameProposalStepAvailabilityAPI } from "../AddTestApi";
import { GameProposalStepAvailabilityResponse } from "../../types/Response";
import { SuccessModal } from "../components/SuccessModal";
import OnJudgingSubjectiveQuestionModal from "./Modal/OnJudgingSubjectiveQuestionModal";
import OnJudgingMultipleQuestionModal from "./Modal/OnJudgingMultipleQuestionModal";

type MultipleQuestion = {
  questionId: number;
  content: string;
  category: string;
  count: number;
  custom: boolean;
  choices: Array<{ id: number; content: string }>;
  rejectionReason: string;
}

const Main = styled.main`
  background-color: #f8f9fa;
  width: 100%;
  min-width: 1280px;
  max-width: 1920px;
  height: 100vh;
  
  padding-left: 195px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TopWrapper = styled.div`
  padding-left: 80px;
  border-bottom: 1px solid #ededed;
  min-height: 157px;
  display: flex;
  flex-direction: column;
  gap: 34px;
  padding-top: 30px;
  background-color: #f8f9fa;
  position: sticky;
  top: 0;
  z-index: 2;
`;

const TopContainer = styled.div`
  // width: 1280px;
  width: 95%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TopTitle = styled.span`
  color: #2282e9;
  font-size: 20px;
  font-weight: 700;
`;

const BtnContainer = styled.div`
  display: flex;
  gap: 20px;
`;

const TopBtn = styled.button`
  width: 160px;
  height: 55px;
  border-radius: 5px;
  background-color: white;
  border: 1px solid #ededed;
  color: #c0c0c0;
  font-weight: 600;
  cursor: pointer;
`;

const PreviewBtn = styled(TopBtn)`
  color: white;
  background-color: #2282e9;
`;

const SaveBtn = styled(TopBtn)`
  color: #2282e9;
  border: 1px solid #2282e9;
`;

const NavCotainer = styled.div`
  color: #c0c0c0;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  width: 1280px;
  height: 37px;
`;

const Nav = styled.div`
  width: 165px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const AccentNav = styled(Nav)`
  border-bottom: 2px solid #2282e9;
  color: #2282e9;
`;

const ContentWrapper = styled.div`
  padding-top: 70px;
  padding-left: 125px;
  padding-bottom: 309px;
  overflow-y: auto;
  height: calc(100vh - 157px);
`;

const Title = styled.span`
  font-weight: 600;
  font-size: 18px;
`;

const Required = styled.span`
  color: #2282e9;
  font-weight: 600;
  font-size: 18px;
`;

const Comment = styled.span`
  display: block;
  line-height: 26px;
  color: #9f9f9f;
  margin-top: 8px;
  margin-bottom: 20px;
`;

const QuestionWrapper = styled.div`
  display: flex;
  gap: 14px;
  margin-bottom: 70px;
`;

const QuestionContainer = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 358px;
  height: 209px;
  border-radius: 10px;
  border: 1.5px solid ${props => props.isSelected ? '#8e8e8e' : '#e3e3e3'};
  background-color: white;
  cursor: not-allowed;
`;

const QuestionTitle = styled.span`
  display: block;
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 29px;
`;
const QuestionContent = styled.span`
  display: block;
  font-weight: 500;
  font-size: 14px;
  color: #8e8e8e;
  margin-bottom: 34px;
`;

const QuestionBtn = styled.button<{ isActive: boolean }>`
  border-radius: 999px;
  width: 223px;
  height: 45px;
  border: none;
  color: #FFFFFF;
  font-weight: 600;
  font-size: 14px;
  cursor: not-allowed;
  background-color: ${props => props.isActive ? '#8E8E8E' : '#D1D1D1'};
`;

const QuestionBox = styled.div`
  // width: 1280px;
  width: 90%;
  height: 300px;
  border-radius: 10px;
  background-color: white;
  border: 1px solid #EBF5FF;
  margin-bottom: 10px;
  overflow-y: auto;
  padding: 12px;
  margin-bottom: 5%;
`;

const DraggableList = styled.div`
  width: 100%;
  position: relative;
`;

const DraggableItem = styled.div`
  margin-bottom: 12px;
  position: relative;
  height: 40px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const QuestionItemContainer = styled.div<{ isCustom?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 10px;
  background-color: ${props => props.isCustom ? '#2282E9' : '#EBF5FF'};
  border-radius: 4px;
  user-select: none;
  height: 40px;
  box-sizing: border-box;
`;

const QuestionText = styled.div<{ hasDeleteButton?: boolean }>`
  flex: 1;
  font-size: 14px;
  color: ${props => props.hasDeleteButton ? 'white' : '#333'};
  margin-left: 50px};
`;

const ActionButton = styled.button`
  width: 120px;
   padding: 4px 8px;
  height: 35px; // 버튼 높이 증가
  border: 1px solid #8E8E8E;
  margin: -12px 0;
  border-radius: 4px;
  background: white;
  color: #8E8E8E;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  
  &:hover {
    background: #ebf5ff;
  }
`;


interface QuestionOverviewData {
  selectedQuestionSource: 'RECOMMENDED' | 'GENRE_BASED';
  genres: {
    genre: string;
    selected: boolean;
  }[];
  questions: {
    multipleChoiceQuestions: {
      questionId: number;
      content: string;
      category: string;
    }[];
    subjectiveQuestions: {
      questionId: number;
      content: string;
      category: string;
    }[];
  }
  customQuestionFreeLimit: {
    multipleChoiceLimit: number;
    subjectiveLimit: number;
  };
}

interface Question {
  questionId: number;
  content: string;
  category: string;
  custom: boolean;
  rejectionReason: string;
  choices?: Array<{ id: number; content: string }>;
  answerLength?: number;
  count?: number;
}

export default function OnJudgingPhase3() {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [questionDetails, setQuestionDetails] = useState<any>(null);
  const { gameProposalId } = useParams();
  console.log(`phase3 - gameProposalId: `, gameProposalId);

  const { data: QuestionOverviewData } = useQuery({
    queryKey: ["companyInfoOverview", gameProposalId],
    queryFn: async () => {
      const response = await LabsQuestionOverviewAPI(Number(gameProposalId));
      return response.data;
    },
    // initialData: {
    //   "selectedQuestionSource": "GENRE_BASED",
    //   "genres": [{
    //     "genre": "RPG_GAME",
    //     "selected": false
    //   }, {
    //     "genre": "SIMULATION_TYCOON_GAME",
    //     "selected": false
    //   }],
    //   "questions": {
    //     "multipleChoiceQuestions": [
    //       {
    //         "questionId": 1120,
    //         "content": "question-6-MULTIPLE_CHOICE",
    //         "category": "MULTIPLE_CHOICE",
    //         "custom": true,
    //         "rejectionReason": ""
    //       },
    //       {
    //         "questionId": 1119,
    //         "content": "question-6-MULTIPLE_CHOICE",
    //         "category": "MULTIPLE_CHOICE",
    //         "custom": false,
    //         "rejectionReason": ""
    //       }, {
    //         "questionId": 1118,
    //         "content": "question-5-MULTIPLE_CHOICE",
    //         "category": "MULTIPLE_CHOICE",
    //         "custom": false,
    //         "rejectionReason": ""
    //       }, {
    //         "questionId": 1117,
    //         "content": "question-4-MULTIPLE_CHOICE",
    //         "category": "MULTIPLE_CHOICE",
    //         "custom": false,
    //         "rejectionReason": ""
    //       }, {
    //         "questionId": 1116,
    //         "content": "question-3-MULTIPLE_CHOICE",
    //         "category": "MULTIPLE_CHOICE",
    //         "custom": false,
    //         "rejectionReason": ""
    //       }, {
    //         "questionId": 1115,
    //         "content": "question-2-MULTIPLE_CHOICE",
    //         "category": "MULTIPLE_CHOICE",
    //         "custom": false,
    //         "rejectionReason": ""
    //       }, {
    //         "questionId": 1114,
    //         "content": "question-1-MULTIPLE_CHOICE",
    //         "category": "MULTIPLE_CHOICE",
    //         "custom": false,
    //         "rejectionReason": "reason"
    //       }],
    //     "subjectiveQuestions": [
    //       {
    //         "questionId": 1124,
    //         "content": "question-6-SUBJECTIVE",
    //         "category": "SUBJECTIVE",
    //         "custom": true,
    //         "rejectionReason": ""
    //       }, {
    //         "questionId": 1124,
    //         "content": "question-6-SUBJECTIVE",
    //         "category": "SUBJECTIVE",
    //         "custom": false,
    //         "rejectionReason": ""
    //       }, {
    //         "questionId": 1123,
    //         "content": "question-5-SUBJECTIVE",
    //         "category": "SUBJECTIVE",
    //         "custom": false,
    //         "rejectionReason": ""
    //       }, {
    //         "questionId": 1122,
    //         "content": "question-4-SUBJECTIVE",
    //         "category": "SUBJECTIVE",
    //         "custom": false,
    //         "rejectionReason": ""
    //       }, {
    //         "questionId": 1121,
    //         "content": "question-3-SUBJECTIVE",
    //         "category": "SUBJECTIVE",
    //         "custom": false,
    //         "rejectionReason": ""
    //       }, {
    //         "questionId": 1120,
    //         "content": "question-2-SUBJECTIVE",
    //         "category": "SUBJECTIVE",
    //         "custom": false,
    //         "rejectionReason": ""
    //       }, {
    //         "questionId": 1119,
    //         "content": "question-1-SUBJECTIVE",
    //         "category": "SUBJECTIVE",
    //         "custom": false,
    //         "rejectionReason": "reason"
    //       }]
    //   },
    //   "customQuestionFreeLimit": {
    //     "multipleChoiceLimit": 1,
    //     "subjectiveLimit": 2
    //   }
    // },
    enabled: true
    // enabled: !!gameProposalId  // gameProposalId가 있을 때만 쿼리 실행
  });

  useEffect(() => {
    if (QuestionOverviewData?.questions) {
      setSelectedRecommendQuestions(
        QuestionOverviewData.questions.multipleChoiceQuestions || []
      );
      setSelectedSubjectiveQuestions(
        QuestionOverviewData.questions.subjectiveQuestions || []
      );
      
      if(QuestionOverviewData?.selectedQuestionSource === 'RECOMMENDED') {
        setIsRecommendQuestionsVisible(true); 
        setIsSubjectiveQuestionsVisible(true);
      } else {
        setIsRecommendQuestionsVisible(false);
        setIsSubjectiveQuestionsVisible(false);
      }
    }

    console.log(`QuestionOverviewData: `, QuestionOverviewData);
  }, [QuestionOverviewData]);



  const [multipleQuestionModal, setMultipleQuestionModal] = useState(false);
  const [subjectiveQuestionModal, setSubjectiveQuestionModal] = useState(false);

  const [selectedRecommendQuestions, setSelectedRecommendQuestions] = useState<Question[]>([]);
  const [selectedSubjectiveQuestions, setSelectedSubjectiveQuestions] = useState<Question[]>([]);

  const [isRecommendQuestionsVisible, setIsRecommendQuestionsVisible] = useState(false);
  const [isSubjectiveQuestionsVisible, setIsSubjectiveQuestionsVisible] = useState(false);
  const [isGenreBasedQuestionsVisible, setIsGenreBasedQuestionsVisible] = useState(false);

  const [editingMultipleChoiceQuestion, setEditingMultipleChoiceQuestion] = useState<any>(null);
  const [editingSubjectiveQuestion, setEditingSubjectiveQuestion] = useState<any>(null);

  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitModalType, setLimitModalType] = useState<'multiple' | 'subjective'>('multiple');

  const [showGenreModal, setShowGenreModal] = useState(false);

  const [selectedGenre, setSelectedGenre] = useState<string | null>(null);

  const closeMultipleQuestionModal = () => {
    setMultipleQuestionModal(false);
  };


  const closeSubjectiveQuestionModal = () => {
    setSubjectiveQuestionModal(false);
    setEditingSubjectiveQuestion(null);

    // 장르가 선택되지 않은 경우 모든 버튼의 active 상태를 false로 변경
    if (!selectedGenre) {
      setIsRecommendQuestionsVisible(false);
      setIsSubjectiveQuestionsVisible(false);
      setIsGenreBasedQuestionsVisible(false);
    }
  };

  const initialFetchQuestions = {
    "multipleChoiceQuestions": [{
      "questionId": 105,
      "content": "question-1-MULTIPLE_CHOICE",
      "category": "MULTIPLE_CHOICE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 106,
      "content": "question-2-MULTIPLE_CHOICE",
      "category": "MULTIPLE_CHOICE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 107,
      "content": "question-3-MULTIPLE_CHOICE",
      "category": "MULTIPLE_CHOICE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 108,
      "content": "question-4-MULTIPLE_CHOICE",
      "category": "MULTIPLE_CHOICE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 109,
      "content": "question-5-MULTIPLE_CHOICE",
      "category": "MULTIPLE_CHOICE",
      "custom": false,
      "rejectionReason": ""
    }],
    "subjectiveQuestions": [{
      "questionId": 110,
      "content": "question-1-SUBJECTIVE",
      "category": "SUBJECTIVE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 111,
      "content": "question-2-SUBJECTIVE",
      "category": "SUBJECTIVE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 112,
      "content": "question-3-SUBJECTIVE",
      "category": "SUBJECTIVE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 113,
      "content": "question-4-SUBJECTIVE",
      "category": "SUBJECTIVE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 114,
      "content": "question-5-SUBJECTIVE",
      "category": "SUBJECTIVE",
      "custom": false,
      "rejectionReason": ""
    }]
  };

  const initialFetchGenreQuestions = {
    "multipleChoiceQuestions": [{
      "questionId": 105,
      "content": "question-1-MULTIPLE_CHOICE-GENRE",
      "category": "MULTIPLE_CHOICE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 106,
      "content": "question-2-MULTIPLE_CHOICE-GENRE",
      "category": "MULTIPLE_CHOICE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 107,
      "content": "question-3-MULTIPLE_CHOICE-GENRE",
      "category": "MULTIPLE_CHOICE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 108,
      "content": "question-4-MULTIPLE_CHOICE-GENRE",
      "category": "MULTIPLE_CHOICE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 109,
      "content": "question-5-MULTIPLE_CHOICE-GENRE",
      "category": "MULTIPLE_CHOICE",
      "custom": false,
      "rejectionReason": ""
    }],
    "subjectiveQuestions": [{
      "questionId": 110,
      "content": "question-1-SUBJECTIVE-GENRE",
      "category": "SUBJECTIVE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 111,
      "content": "question-2-SUBJECTIVE-GENRE",
      "category": "SUBJECTIVE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 112,
      "content": "question-3-SUBJECTIVE-GENRE",
      "category": "SUBJECTIVE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 113,
      "content": "question-4-SUBJECTIVE-GENRE",
      "category": "SUBJECTIVE",
      "custom": false,
      "rejectionReason": ""
    }, {
      "questionId": 114,
      "content": "question-5-SUBJECTIVE-GENRE",
      "category": "SUBJECTIVE",
      "custom": false,
      "rejectionReason": ""
    }]
  }
  const { data: gameProposalStepAvailabilityData } = useQuery<GameProposalStepAvailabilityResponse>({
    queryKey: ["gameProposalStepAvailability", gameProposalId],
    queryFn: async () => {
      const response = await GameProposalStepAvailabilityAPI(Number(gameProposalId));
      return response.data;
    },
    // initialData: {
    //   "testPlanAvailable": true,
    //   "projectInfoAvailable": false,
    //   "questionCompositionAvailable": false,
    //   "estimateAvailable": false,
    //   "previewAvailable": false,
    //   "submitAvailable": true,
    //   "testPlanSavable": true,
    //   "projectInfoSavable": false,
    //   "questionCompositionSavable": false
    // },
    enabled: true
  });


  const queryClient = useQueryClient();

  const { mutate: fetchQuestions } = useMutation({
    mutationFn: (params: { source: string; genre?: string }) =>
      QuestionsBySourceAPI(params.source, params.genre),
    onSuccess: (response, params) => {
      // 장르가 변경되면 커스텀 질문만 유지하고 나머지는 새로운 질문으로 교체
      const customMultipleQuestions = selectedRecommendQuestions.filter((q: Question) => q.custom);
      const customSubjectiveQuestions = selectedSubjectiveQuestions.filter((q: Question) => q.custom);

      if (response.data?.multipleChoiceQuestions.length === 0 && response.data?.subjectiveQuestions.length === 0) {
        // 질문이 없는 경우 기본 질문 사용
        if (params.source === 'RECOMMENDED') {
          setSelectedRecommendQuestions([...customMultipleQuestions, ...initialFetchQuestions.multipleChoiceQuestions]);
          setSelectedSubjectiveQuestions([...customSubjectiveQuestions, ...initialFetchQuestions.subjectiveQuestions]);
        } else {
          setSelectedRecommendQuestions([...customMultipleQuestions, ...initialFetchGenreQuestions.multipleChoiceQuestions]);
          setSelectedSubjectiveQuestions([...customSubjectiveQuestions, ...initialFetchGenreQuestions.subjectiveQuestions]);
        }
      } else {
        // 질문이 있는 경우 응답 데이터 사용
        const { multipleChoiceQuestions, subjectiveQuestions } = response.data;
        setSelectedRecommendQuestions([...customMultipleQuestions, ...multipleChoiceQuestions]);
        setSelectedSubjectiveQuestions([...customSubjectiveQuestions, ...subjectiveQuestions]);
      }
    },
    onError: (error) => {
      console.error("질문 구성 저장 실패:", error);
    }
  });

  const hasSelectedGenre = (data: typeof QuestionOverviewData): boolean => {
    if (!data || !data.genres || data.genres.length === 0) {
      return false;
    }

    return data.genres.some((genre: { genre: string; selected: boolean }) => genre.selected === true);
  };


  const handleRecommendQuestion = () => {

    setIsGenreBasedQuestionsVisible(false);
    setSelectedGenre(null);
    if (!isRecommendQuestionsVisible) {
      setIsRecommendQuestionsVisible(true);
      setIsSubjectiveQuestionsVisible(true);

      fetchQuestions({ source: 'RECOMMENDED' });
    } else {
      setIsRecommendQuestionsVisible(false);
      setIsSubjectiveQuestionsVisible(false);

      setSelectedRecommendQuestions(prev => prev.filter(q => q.custom));
      setSelectedSubjectiveQuestions(prev => prev.filter(q => q.custom));

    }
  };

  const handleGenreBasedQuestion = () => {
    console.log(`QuestionOverviewData?.genres: ${QuestionOverviewData?.genres}`);
    if (selectedGenre) {
      setIsRecommendQuestionsVisible(false);
    }
    setIsGenreBasedQuestionsVisible(true);
    setShowGenreModal(true);
  };

  const handleDeleteMultipleChoiceQuestion = (questionId: number) => {
    const updatedQuestions = selectedRecommendQuestions.filter(question => question.questionId !== questionId);
    setSelectedRecommendQuestions(updatedQuestions);
    console.log(`Deleting question with ID: ${questionId}`);
  };

  const handleEditMultipleChoiceQuestion = (question: any) => {
    const questionToEdit = {
      ...question,
      choices: question.choices || [],
      allowMultiple: question.allowMultiple || 1
    };
    setEditingMultipleChoiceQuestion(questionToEdit);
    setMultipleQuestionModal(true);

    setQuestionDetails(mockQuestionDetails);
    // fetchQuestionDetailMutation.mutate(question.id); // TODO
    // console.log(questionDetail);
  };

  const mockQuestionDetails = {
    "id" : 377,
    "content" : "question-1-MULTIPLE_CHOICE",
    "allowedChoices" : 1,
    "category" : "MULTIPLE_CHOICE",
    "choices" : [ {
      "id" : 166,
      "content" : "choice content-377-0"
    }, {
      "id" : 167,
      "content" : "choice content-377-1"
    }, {
      "id" : 168,
      "content" : "choice content-377-2"
    }, {
      "id" : 169,
      "content" : "choice content-377-3"
    }, {
      "id" : 170,
      "content" : "choice content-377-4"
    } ],
    "minAnswerLength" : 1
  };

  const fetchQuestionDetailMutation = useMutation({
    mutationFn: (questionId: number) => QuestionDetailAPI(questionId),
    onSuccess: (response) => {
      // setQuestionDetails(response.data);
      
      console.log(response.data);
    },
  });


  const handleDeleteSubjectiveQuestion = (questionId: number) => {
    const updatedQuestions = selectedSubjectiveQuestions.filter(question => question.questionId !== questionId);
    setSelectedSubjectiveQuestions(updatedQuestions);
  };

  const handleEditSubjectiveQuestion = (question: any) => {
    setEditingSubjectiveQuestion(question);
    setSubjectiveQuestionModal(true);

    setQuestionDetails(mockQuestionDetails);
  };

  const handleSave = () => {
    console.log('현재 객관식 질문 목록:', selectedRecommendQuestions);
    console.log('현재 주관식 질문 목록:', selectedSubjectiveQuestions);
    console.log("저장하기 버튼 누름");

    const formattedMultipleQuestions = selectedRecommendQuestions.map(question => {
      if (question.custom) {
        return {
          questionId: null,
          customQuestion: {
            questionId: null,
            content: question.content,
            allowedChoices: question.count,
            category: "MULTIPLE_CHOICE",
            choices: (question.choices || []).map(choice => ({ content: choice.content })),
            minAnswerLength: 0
          }
        };
      } else {
        return {
          questionId: question.questionId,
          customQuestion: null
        };
      }
    });

    const formattedSubjectiveQuestions = selectedSubjectiveQuestions.map(question => {
      if (question.custom) {
        return {
          questionId: null,
          customQuestion: {
            questionId: null,
            content: question.content,
            allowedChoices: 0,
            category: "SUBJECTIVE",
            choices: null,
            minAnswerLength: 10
          }
        };
      } else {
        return {
          questionId: question.questionId,
          customQuestion: null
        };
      }
    });

    const data = {
      questionSource: selectedGenre ? 'GENRE_BASED' : 'RECOMMENDED',
      genre: selectedGenre ? selectedGenre : 'NONE',
      surveyCategory: 'GAME_TEST',
      multipleQuestions: formattedMultipleQuestions,
      subjectiveQuestions: formattedSubjectiveQuestions
    }
    // LabsQuestionComposeAPI(gameProposalId!, data);
    const response = LabsQuestionComposeAPIMutation.mutate(data);
    console.log(response);
  }

  const LabsQuestionComposeAPIMutation = useMutation({
    mutationFn: (data: any) => LabsQuestionComposeAPI(Number(gameProposalId), data),
    onSuccess: (response) => {
      if (response.status === 200) {
        setShowSuccessModal(true);
        console.log("3단계 저장 성공");

        queryClient.invalidateQueries({
          queryKey: ["gameProposalStepAvailability", gameProposalId]
        });

        queryClient.refetchQueries({
          queryKey: ["gameProposalStepAvailability", gameProposalId]
        });
      }
    },
    onError: (error) => {
      console.log("저장 실패");
    },
  });


  const navigate = useNavigate();

  return (
    <Main>
      {multipleQuestionModal && (
        <OnJudgingMultipleQuestionModal
          close={closeMultipleQuestionModal}
          data={questionDetails}
        />
      )}

      {subjectiveQuestionModal && (
        <OnJudgingSubjectiveQuestionModal 
        close={closeSubjectiveQuestionModal}
          data={questionDetails}
        />
      )}

      <LimitModal
        isOpen={showLimitModal}
        onClose={() => setShowLimitModal(false)}
        onConfirm={() => {
          setEditingMultipleChoiceQuestion(null);
          setEditingSubjectiveQuestion(null);
          setShowLimitModal(false);
          if (limitModalType === 'multiple') {
            setMultipleQuestionModal(true);
          } else {
            setSubjectiveQuestionModal(true);
          }
        }}
        type={limitModalType}
        multipleLimit={QuestionOverviewData?.customQuestionFreeLimit?.multipleChoiceLimit || 0}
        subjectiveLimit={QuestionOverviewData?.customQuestionFreeLimit?.subjectiveLimit || 0}
      />

      <GenreModal
        isOpen={showGenreModal}
        onClose={() => {
          setShowGenreModal(false);
          if (!selectedGenre) {
            setIsGenreBasedQuestionsVisible(false);
          }
        }}
        onConfirm={(selectedGenre: string) => {
          // 장르가 변경되면 기존 질문 목록 초기화
          setSelectedRecommendQuestions(prev => prev.filter(q => q.custom));
          setSelectedSubjectiveQuestions(prev => prev.filter(q => q.custom));

          if (selectedGenre) {
            setIsRecommendQuestionsVisible(false);
          }
          setSelectedGenre(selectedGenre);

          fetchQuestions({
            source: 'GENRE_BASED',
            genre: selectedGenre
          });
          setShowGenreModal(false);
        }}
        genres={QuestionOverviewData?.genres || []}
      />
      <TopWrapper>
        <TopContainer>
        <Link to={`/home/test/draft/${gameProposalId}`} style={{ textDecoration: "none" }}>
            <TopTitle>
              <FontAwesomeIcon
                icon={faChevronLeft}
                style={{ marginRight: "20px" }}
              />
              3단계 질문 구성
            </TopTitle>
          </Link>
          <BtnContainer>
            {/* <PreviewBtn onClick={() => navigate(`/home/test/preview/${gameProposalId}`)}>미리보기</PreviewBtn> */}
            <SaveBtn onClick={handleSave}>저장하기</SaveBtn>
          </BtnContainer>
        </TopContainer>
        <NavCotainer>
          <CustomNavLink
            to={`/home/test-list/${gameProposalId}/add/first`}
            status={gameProposalStepAvailabilityData?.testPlanAvailable}
          >
            1. 테스트 플랜
          </CustomNavLink>
          <CustomNavLink
            to={`/home/test-list/${gameProposalId}/add/second`}
            status={gameProposalStepAvailabilityData?.projectInfoAvailable}
          >
            2. 프로젝트 정보
          </CustomNavLink>
          <CustomNavLink
            to={`/home/test-list/${gameProposalId}/add/third/on-judging`}
            status={gameProposalStepAvailabilityData?.questionCompositionAvailable}
            isActive={true}
          >
            3. 질문 구성
          </CustomNavLink>
          <CustomNavLink
            to={`/home/test-list/${gameProposalId}/add/forth`}
            status={gameProposalStepAvailabilityData?.estimateAvailable}
          >
            4. 견적 확인
          </CustomNavLink>
        </NavCotainer>
      </TopWrapper>
      <ContentWrapper>
        <Title>
          질문 구성
          <Required> *</Required>
        </Title>
        <Comment>
          테스트를 구성할 질문들을 조합해 질문을 구성해주세요.
          <br />
          자동으로 질문이 구성됩니다. 추후 원하는 질문은 직접 추가가 가능합니다.
        </Comment>
        <QuestionWrapper>
          <QuestionContainer isSelected={QuestionOverviewData?.selectedQuestionSource === 'RECOMMENDED'}>
            <QuestionTitle>추천 질문</QuestionTitle>
            <QuestionContent>플리더스가 추천하는 질문</QuestionContent>
            <QuestionBtn isActive={QuestionOverviewData?.selectedQuestionSource === 'RECOMMENDED'}>{QuestionOverviewData?.selectedQuestionSource === 'RECOMMENDED' ? '선택완료' : '선택하기'}</QuestionBtn>
          </QuestionContainer>
          <QuestionContainer isSelected={QuestionOverviewData?.selectedQuestionSource === 'GENRE_BASED'}>
            <QuestionTitle>장르별 질문</QuestionTitle>
            <QuestionContent>장르별 효과적인 질문</QuestionContent>
            <QuestionBtn isActive={QuestionOverviewData?.selectedQuestionSource === 'GENRE_BASED'}>{QuestionOverviewData?.selectedQuestionSource === 'GENRE_BASED' ? '선택완료' : '선택하기'}</QuestionBtn>
          </QuestionContainer>
        </QuestionWrapper>
        <Title>객관식 질문</Title>
        <Comment>현재 총 {selectedRecommendQuestions.length} 문항</Comment>
        <QuestionBox>
          <DraggableList>
            {selectedRecommendQuestions.map((question, index) => (
              <DraggableItem key={question.questionId.toString()}>
                <QuestionItemContainer isCustom={question.custom}
                  style={{
                    background: question.custom ? '#8E8E8E' : '#F8F8F8',
                  }}
                >
                  <QuestionText hasDeleteButton={question.custom}>
                    {question.content}
                  </QuestionText>
                  {question.custom && (
                    <ActionButton onClick={() => handleEditMultipleChoiceQuestion(question)}>
                      확인
                    </ActionButton>
                  )}
                </QuestionItemContainer>
              </DraggableItem>
            ))}
          </DraggableList>
        </QuestionBox>

        <Title>주관식 질문</Title>
        <Comment>현재 총 {selectedSubjectiveQuestions.length} 문항</Comment>
        <QuestionBox>
          <DraggableList>
            {selectedSubjectiveQuestions.map((question, index) => (
              <DraggableItem key={question.questionId.toString()}>
                <QuestionItemContainer isCustom={question.custom}
                  style={{
                    background: question.custom ? '#8E8E8E' : '#F8F8F8',
                  }}
                >

                  <QuestionText hasDeleteButton={question.custom}>
                    {question.content}
                  </QuestionText>
                  {question.custom && (
                    <ActionButton onClick={() => handleEditSubjectiveQuestion(question)}>
                      확인
                    </ActionButton>
                  )}
                </QuestionItemContainer>
              </DraggableItem>
            ))}
          </DraggableList>
        </QuestionBox>

      </ContentWrapper>
    </Main>
  );

}

